// IMPORTANT: After modifying this file, update CHANGELOG.md with a summary of your changes.
// Add clear comments for non-obvious changes, including what replaced prior behavior, to avoid repeating failed fixes.

"use client";

import { SmartLink } from "@/components/SmartLink";

interface NavTreeItem {
  id: number;
  label: string;
  href: string;
  isExternal: boolean;
  children: { id: number; label: string; href: string; isExternal: boolean }[];
}

/**
 * Horizontal nav with hover/focus-within dropdowns for the HEADER REGION
 * DESKTOP layout only. The header shell wraps this in a `hidden md:flex`
 * so it never shows below the `md` breakpoint — touch devices have no
 * hover, so the shell swaps in <NavLinksMobile> for the mobile panel.
 *
 * Previously this component owned its own `hidden md:flex` wrapper; the
 * shell-driven approach is cleaner because the shell is the single place
 * that knows "am I on mobile?" and can pick the right layout.
 */
export function NavLinksInline({ items }: { items: NavTreeItem[] }) {
  return (
    <div className="flex gap-8 text-sm uppercase tracking-widest items-center">
      {items.map((item) =>
        item.children.length > 0 ? (
          <div key={item.id} className="relative group">
            <div className="flex items-center gap-2">
              <SmartLink
                href={item.href}
                className="hover:text-stone-600 transition-colors"
                {...(item.isExternal
                  ? { target: "_blank", rel: "noopener noreferrer" }
                  : {})}
              >
                {item.label}
              </SmartLink>
              <button
                type="button"
                className="hover:text-stone-600 transition-colors"
                aria-haspopup="menu"
                aria-label={`Open ${item.label} menu`}
              >
                <span className="text-xs">▾</span>
              </button>
            </div>
            <div className="absolute left-0 top-full pt-2 hidden group-hover:block group-focus-within:block z-50">
              <div className="min-w-[240px] brutal-border-sm bg-white p-2">
                {item.children.map((child) => (
                  <SmartLink
                    key={child.id}
                    href={child.href}
                    className="block px-3 py-2 hover:bg-stone-100 transition-colors"
                    {...(child.isExternal
                      ? { target: "_blank", rel: "noopener noreferrer" }
                      : {})}
                  >
                    {child.label}
                  </SmartLink>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <SmartLink
            key={item.id}
            href={item.href}
            className="hover:text-stone-600 transition-colors"
            {...(item.isExternal
              ? { target: "_blank", rel: "noopener noreferrer" }
              : {})}
          >
            {item.label}
          </SmartLink>
        )
      )}
    </div>
  );
}
